// TODO: i18n — see Auth.js for pattern, replace hardcoded strings with t('namespace.key')
import { useState, useRef, useEffect } from 'react'
import { Thermometer, Refrigerator, Droplets, WashingMachine, Wind, Sparkles, Flame, Microwave, RefreshCw, AlarmSmoke, DoorClosed, Wrench, Camera, FolderOpen } from 'lucide-react'
import { supabase } from '../supabase'
import { compressImage } from '../imageUtils'
import { recordProvenance } from '../utils/photoProvenance'
import { stripExif } from '../utils/stripExif'
import { createAppliance, updateApplianceAsset } from '../utils/applianceQueries'
import {
  getGenericInstructions, normalizeBrand,
  applianceTypeCandidates, lookupBestInstructions,
} from '../utils/dataPlateLocations'
import { convertHeicIfNeeded } from '../utils/convertHeicIfNeeded'
import { emitTourEvent, TOUR_EVENT_TYPES } from '../lib/appEvents'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', success: '#2E6B4A', danger: '#B03020',
}
const f = 'Georgia, serif'

const TYPES = [
  { id: 'hvac', label: 'HVAC / Furnace', Icon: Thermometer },
  { id: 'refrigerator', label: 'Refrigerator', Icon: Refrigerator },
  { id: 'water_heater', label: 'Water Heater', Icon: Droplets },
  { id: 'washer', label: 'Washer', Icon: WashingMachine },
  { id: 'dryer', label: 'Dryer', Icon: Wind },
  { id: 'dishwasher', label: 'Dishwasher', Icon: Sparkles },
  { id: 'oven', label: 'Oven / Range', Icon: Flame },
  { id: 'microwave', label: 'Microwave', Icon: Microwave },
  { id: 'garbage_disposal', label: 'Garbage Disposal', Icon: RefreshCw },
  { id: 'smoke_detector', label: 'Smoke Detector', Icon: AlarmSmoke },
  { id: 'garage_door', label: 'Garage Door', Icon: DoorClosed },
  { id: 'other', label: 'Other', Icon: Wrench },
]

const maintenanceSchedules = {
  hvac: [
    { task: 'Filter Replacement', interval: 'Every 90 days', type: 'auto_order' },
    { task: 'Coil Cleaning', interval: 'Annually — Spring', type: 'contractor' },
    { task: 'Full System Inspection', interval: 'Annually — Fall', type: 'contractor' },
  ],
  refrigerator: [
    { task: 'Water Filter Replacement', interval: 'Every 6 months', type: 'auto_order' },
    { task: 'Condenser Coil Cleaning', interval: 'Annually', type: 'diy' },
  ],
  water_heater: [
    { task: 'Tank Flush', interval: 'Annually', type: 'diy' },
    { task: 'Anode Rod Inspection', interval: 'Every 2 years', type: 'contractor' },
  ],
  washer: [
    { task: 'Drum Cleaning Cycle', interval: 'Monthly', type: 'diy' },
    { task: 'Inlet Hose Inspection', interval: 'Annually', type: 'diy' },
  ],
  dryer: [
    { task: 'Lint Trap Cleaning', interval: 'Every use', type: 'diy' },
    { task: 'Vent Duct Cleaning', interval: 'Annually', type: 'contractor' },
  ],
  dishwasher: [
    { task: 'Filter Cleaning', interval: 'Monthly', type: 'diy' },
    { task: 'Descaling Treatment', interval: 'Every 6 months', type: 'auto_order' },
  ],
  smoke_detector: [
    { task: 'Battery Replacement', interval: 'Annually', type: 'auto_order' },
    { task: 'Test & Inspection', interval: 'Monthly', type: 'diy' },
  ],
  garage_door: [
    { task: 'Lubricate Moving Parts', interval: 'Every 6 months', type: 'auto_order' },
    { task: 'Full Service Inspection', interval: 'Annually', type: 'contractor' },
  ],
  oven: [
    { task: 'Deep Clean', interval: 'Every 3 months', type: 'diy' },
    { task: 'Burner Inspection', interval: 'Annually', type: 'diy' },
  ],
  microwave: [
    { task: 'Interior Clean', interval: 'Monthly', type: 'diy' },
    { task: 'Filter Replacement', interval: 'Every 6 months', type: 'auto_order' },
  ],
  garbage_disposal: [
    { task: 'Cleaning & Deodorizing', interval: 'Monthly', type: 'auto_order' },
    { task: 'Blade Inspection', interval: 'Annually', type: 'diy' },
  ],
  other: [
    { task: 'General Inspection', interval: 'Annually', type: 'diy' },
  ],
}

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.5)',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: C.card, borderRadius: '20px 20px 0 0',
    width: '100%', maxWidth: '600px',
    maxHeight: '90vh', overflowY: 'auto',
    padding: '28px 24px 40px',
  },
  handle: {
    width: '40px', height: '4px', background: C.border,
    borderRadius: '2px', margin: '0 auto 20px',
  },
  title: { fontSize: '22px', fontWeight: '700', color: C.brown, marginBottom: '6px' },
  subtitle: { fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '24px' },
  // Step 2 header: title on the left, neutral close X on the right. Tapping
  // the X silently saves whatever the user has so far — partial records show
  // up on the dashboard with the existing "Incomplete" badge.
  step2Header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px',
  },
  step2CloseX: {
    background: 'transparent', border: 'none', cursor: 'pointer',
    color: C.muted, fontSize: '20px', lineHeight: 1,
    padding: '4px 8px', borderRadius: '8px',
    fontFamily: 'Calibri, sans-serif', flexShrink: 0, marginTop: '-2px',
  },
  stepDots: { display: 'flex', gap: '6px', marginBottom: '24px' },
  dot: (active, done) => ({
    width: '8px', height: '8px', borderRadius: '50%',
    background: done ? C.amber : active ? C.amber : C.border,
    opacity: done ? 1 : active ? 1 : 0.4,
    transition: 'all 0.2s',
  }),
  typeGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '10px', marginBottom: '24px',
  },
  typeCard: (selected) => ({
    background: selected ? '#FBF3E8' : C.bg,
    border: `1px solid ${selected ? C.amber : C.border}`,
    borderRadius: '12px', padding: '14px 8px',
    cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s',
  }),
  typeIcon: { display: 'flex', justifyContent: 'center', marginBottom: '6px' },
  typeLabel: (selected) => ({
    fontSize: '11px', fontFamily: 'Calibri, sans-serif',
    color: selected ? C.amber : C.brown,
    fontWeight: selected ? '700' : '400', lineHeight: 1.3,
  }),
  scanBanner: {
    background: 'linear-gradient(135deg, #FBF3E8, #FDF6EE)',
    border: `1px solid ${C.amber}44`,
    borderRadius: '14px', padding: '18px 20px',
    marginBottom: '12px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '14px',
    transition: 'all 0.15s',
  },
  scanIcon: { display: 'flex', alignItems: 'center', flexShrink: 0 },
  scanText: { flex: 1 },
  scanTitle: { fontSize: '15px', fontWeight: '700', color: C.brown, marginBottom: '2px' },
  scanSub: { fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif' },
  scanArrow: { fontSize: '18px', color: C.amber },
  uploadRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '8px', marginBottom: '16px', cursor: 'pointer',
    fontSize: '13px', color: C.amber, fontFamily: 'Calibri, sans-serif',
  },
  // Equal-weight escape hatches under the primary scan card. Casavio principle:
  // wherever camera-roll upload is offered, manual-entry must be offered at the
  // same visual weight.
  entryLinks: {
    display: 'flex', flexWrap: 'wrap', justifyContent: 'center',
    gap: '8px 20px', marginBottom: '16px',
  },
  entryLink: {
    background: 'transparent', border: 'none', padding: '4px 4px',
    fontSize: '13px', color: C.amber, fontFamily: 'Calibri, sans-serif',
    textDecoration: 'underline', cursor: 'pointer',
  },
  scanResult: {
    background: '#EBF5EF', border: `1px solid ${C.success}44`,
    borderRadius: '14px', padding: '16px 18px', marginBottom: '20px',
  },
  scanResultTitle: {
    fontSize: '12px', color: C.success, fontFamily: 'Calibri, sans-serif',
    letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '10px', fontWeight: '700',
  },
  scanResultField: {
    display: 'flex', justifyContent: 'space-between',
    fontSize: '13px', fontFamily: 'Calibri, sans-serif',
    color: C.brown, marginBottom: '4px',
  },
  scanResultLabel: { color: C.muted },
  divider: {
    display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px',
  },
  dividerLine: { flex: 1, height: '1px', background: C.border },
  dividerText: { fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif' },
  label: {
    fontSize: '11px', color: C.muted, letterSpacing: '1.5px',
    textTransform: 'uppercase', fontFamily: 'Calibri, sans-serif',
    marginBottom: '6px', display: 'block',
  },
  input: (focused) => ({
    width: '100%', padding: '12px 14px',
    border: `1px solid ${focused ? C.amber : C.border}`,
    borderRadius: '10px', fontSize: '16px',
    fontFamily: 'Calibri, sans-serif', color: C.brown,
    background: C.card, outline: 'none',
    boxSizing: 'border-box', marginBottom: '14px',
    transition: 'border 0.15s',
    WebkitUserSelect: 'text', userSelect: 'text', touchAction: 'manipulation',
    WebkitAppearance: 'none',
  }),
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  btnRow: { display: 'flex', gap: '10px', marginTop: '24px' },
  btnBack: {
    flex: 1, background: 'transparent', border: `1px solid ${C.border}`,
    borderRadius: '10px', padding: '13px', fontSize: '13px',
    color: C.muted, cursor: 'pointer', fontFamily: f,
  },
  btnNext: (disabled) => ({
    flex: 2,
    background: disabled ? C.border : `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
    border: 'none', borderRadius: '10px', padding: '13px',
    fontSize: '14px', fontWeight: '700', letterSpacing: '1px',
    textTransform: 'uppercase', color: disabled ? C.muted : C.card,
    cursor: disabled ? 'default' : 'pointer', fontFamily: f, transition: 'all 0.2s',
  }),
  scheduleCard: {
    background: C.bg, border: `1px solid ${C.border}`,
    borderRadius: '10px', padding: '12px 14px', marginBottom: '8px',
  },
  scheduleTask: { fontSize: '13px', fontWeight: '600', color: C.brown, marginBottom: '2px' },
  scheduleMeta: { fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif' },
  tag: (type) => {
    const colors = {
      auto_order: { bg: '#FBF3E8', color: C.amber },
      contractor: { bg: '#EBF5EF', color: C.success },
      diy: { bg: C.bg, color: C.muted },
    }
    const c = colors[type] || colors.diy
    return {
      display: 'inline-block', fontSize: '10px', padding: '2px 8px',
      borderRadius: '20px', background: c.bg, color: c.color,
      fontFamily: 'Calibri, sans-serif', border: `1px solid ${c.color}44`, marginTop: '4px',
    }
  },
  alert: {
    background: '#FDECEA', border: `1px solid ${C.danger}44`,
    borderRadius: '10px', padding: '10px 14px', fontSize: '13px',
    color: C.danger, fontFamily: 'Calibri, sans-serif', marginBottom: '14px',
  },

  // ── LIVE CAMERA ──
  cameraOverlay: {
    position: 'fixed', inset: 0, background: '#000',
    zIndex: 2000, display: 'flex', flexDirection: 'column',
    alignItems: 'stretch',
  },
  cameraHeader: {
    width: '100%', padding: '14px 20px',
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', flexShrink: 0,
  },
  stepBadge: {
    color: '#fff', fontSize: '12px', fontWeight: '700',
    fontFamily: 'Calibri, sans-serif', letterSpacing: '2.5px',
    background: 'rgba(184,114,31,0.85)',
    padding: '6px 12px', borderRadius: '14px',
  },
  cameraClose: {
    color: '#fff', fontSize: '22px', background: 'rgba(255,255,255,0.1)',
    border: 'none', cursor: 'pointer', padding: '4px 12px',
    lineHeight: 1, borderRadius: '20px',
  },
  // Wraps the instruction pill; centers it horizontally and gives breathing
  // room above the viewfinder. Pill itself is the visual hero of the screen.
  instructionPillWrap: {
    width: '100%', display: 'flex', justifyContent: 'center',
    padding: '4px 16px 14px', flexShrink: 0,
  },
  instructionPill: {
    color: '#fff', fontSize: '21px', fontWeight: '600',
    fontFamily: 'Calibri, sans-serif', textAlign: 'center',
    lineHeight: 1.35, letterSpacing: '0.3px',
    background: 'rgba(0,0,0,0.65)',
    padding: '14px 18px', borderRadius: '12px',
    maxWidth: '90vw',
    boxShadow: '0 2px 12px rgba(0,0,0,0.35)',
    animation: 'casavio-instructionFadeIn 320ms ease-out',
  },
  // Subtle in-flight indicator for the optimistic background photo 1 identify.
  backgroundIdPill: {
    position: 'absolute', left: '50%', bottom: '14px',
    transform: 'translateX(-50%)',
    background: 'rgba(0,0,0,0.6)', color: 'rgba(255,255,255,0.92)',
    fontSize: '12px', fontFamily: 'Calibri, sans-serif',
    padding: '6px 12px 6px 10px', borderRadius: '14px',
    display: 'inline-flex', alignItems: 'center', gap: '8px',
    zIndex: 11,
    animation: 'casavio-pillFadeIn 220ms ease-out',
  },
  miniSpinner: {
    width: 12, height: 12, borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: '#fff',
    animation: 'casavio-spin 0.9s linear infinite',
  },
  videoContainer: {
    position: 'relative', width: '100%', flex: 1,
    overflow: 'hidden',
  },
  video: {
    width: '100%', height: '100%',
    objectFit: 'cover', display: 'block',
  },
  cameraBottom: {
    width: '100%',
    padding: '18px 24px calc(24px + env(safe-area-inset-bottom, 0px))',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: '12px', flexShrink: 0,
    background: '#000',
  },
  shutter: (enabled) => ({
    width: '74px', height: '74px', borderRadius: '50%',
    background: 'transparent',
    border: `4px solid ${enabled ? '#fff' : 'rgba(255,255,255,0.4)'}`,
    cursor: enabled ? 'pointer' : 'default',
    padding: 0, position: 'relative',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'transform 0.1s',
  }),
  shutterInner: {
    width: '56px', height: '56px', borderRadius: '50%',
    background: '#fff', display: 'block',
    transition: 'transform 0.1s',
  },
  uploadFallback: {
    fontSize: '13px', color: 'rgba(255,255,255,0.75)',
    background: 'transparent', border: 'none',
    fontFamily: 'Calibri, sans-serif', cursor: 'pointer',
    textDecoration: 'underline', padding: '4px 8px',
  },
  cameraEscapeRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '6px', flexWrap: 'wrap',
  },
  cameraEscapeSep: {
    color: 'rgba(255,255,255,0.4)', fontSize: '13px',
    fontFamily: 'Calibri, sans-serif',
  },
  torchBtn: (on) => ({
    position: 'absolute', top: 12, right: 12,
    width: 52, height: 52, borderRadius: '50%',
    background: on ? C.amber : 'rgba(255,255,255,0.15)',
    border: `2px solid ${on ? C.amber : 'rgba(255,255,255,0.45)'}`,
    color: '#fff', fontSize: 24, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 10, transition: 'all 0.2s',
  }),
  // Photo 1 thumbnail (visible after photo 1 captured)
  thumbWrap: {
    position: 'absolute', top: 12, left: 12,
    width: 72, height: 72, borderRadius: '10px',
    border: '2px solid #fff',
    background: '#000',
    padding: 0, cursor: 'pointer',
    overflow: 'hidden', zIndex: 10,
    boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
  },
  thumbImg: {
    width: '100%', height: '100%', objectFit: 'cover', display: 'block',
  },
  thumbBadge: {
    position: 'absolute', bottom: 2, right: 2,
    background: '#2E6B4A', color: '#fff',
    fontSize: 10, fontWeight: 700, fontFamily: 'Calibri, sans-serif',
    padding: '2px 5px', borderRadius: '6px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.5)',
  },
  // Retake confirm menu
  retakeOverlay: {
    position: 'absolute', inset: 0,
    background: 'rgba(0,0,0,0.65)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 20, zIndex: 20,
  },
  retakeMenu: {
    background: C.card, borderRadius: 14,
    padding: 20, maxWidth: 320, width: '100%',
    display: 'flex', flexDirection: 'column', gap: 10,
    boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
  },
  retakeTitle: {
    fontSize: 12, color: C.muted, fontFamily: 'Calibri, sans-serif',
    letterSpacing: 2, textTransform: 'uppercase',
  },
  retakePreview: {
    width: '100%', aspectRatio: '4/3', objectFit: 'cover',
    borderRadius: 10, border: `1px solid ${C.border}`,
  },
  retakePrimary: {
    background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
    color: C.card, border: 'none', borderRadius: 10,
    padding: 12, fontSize: 13, fontWeight: 700,
    fontFamily: f, letterSpacing: 1, textTransform: 'uppercase',
    cursor: 'pointer',
  },
  retakeSecondary: {
    background: 'transparent', color: C.muted,
    border: `1px solid ${C.border}`, borderRadius: 10,
    padding: 11, fontSize: 13, fontFamily: 'Calibri, sans-serif',
    cursor: 'pointer',
  },
  lightingHint: {
    fontSize: '12px', color: 'rgba(255,255,255,0.65)',
    fontFamily: 'Calibri, sans-serif', textAlign: 'center',
    maxWidth: '300px', lineHeight: 1.45, padding: '0 8px',
  },
  // Photo 2 still image covers the live <video> during narration.
  photo2Still: {
    position: 'absolute', inset: 0,
    width: '100%', height: '100%',
    objectFit: 'cover', display: 'block', zIndex: 5,
  },
  // Narration stack: top-left overlay above the photo 2 still.
  narrationStack: {
    position: 'absolute', top: 16, left: 16,
    right: 16, // allow stacking up to ~edge of screen on narrow devices
    display: 'flex', flexDirection: 'column',
    alignItems: 'flex-start', gap: 8,
    zIndex: 15, pointerEvents: 'none', // children opt back in for the button
  },
  narrationLine: {
    display: 'inline-flex', alignItems: 'center', gap: 10,
    background: 'rgba(0,0,0,0.7)', color: '#fff',
    fontFamily: 'Calibri, sans-serif', fontSize: 16, fontWeight: 500,
    lineHeight: 1.4, padding: '8px 14px',
    borderRadius: 12, maxWidth: '100%',
    boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
    animation: 'casavio-narrationLineIn 250ms ease-out',
    pointerEvents: 'auto',
  },
  narrationIconFound: {
    color: '#4ADE80', fontSize: 16, fontWeight: 700,
    flexShrink: 0, lineHeight: 1,
  },
  narrationIconMuted: {
    color: 'rgba(255,255,255,0.55)', fontSize: 16,
    flexShrink: 0, lineHeight: 1,
  },
  narrationText: {
    color: '#fff',
  },
  narrationField: {
    fontWeight: 600,
  },
  reviewBtn: {
    background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
    color: '#fff', border: 'none', borderRadius: 14,
    padding: '14px 22px', fontSize: 15, fontWeight: 700,
    fontFamily: f, letterSpacing: 1, textTransform: 'uppercase',
    cursor: 'pointer', marginTop: 6,
    boxShadow: '0 4px 14px rgba(184,114,31,0.4)',
    animation: 'casavio-reviewBtnIn 350ms ease-out, casavio-reviewBtnPulse 2s ease-in-out 350ms infinite',
    pointerEvents: 'auto',
  },
  retakePhoto2Btn: {
    background: 'transparent', color: '#fff',
    border: '1px solid rgba(255,255,255,0.45)',
    borderRadius: 10, padding: '10px 16px',
    fontSize: 13, fontWeight: 600,
    fontFamily: 'Calibri, sans-serif',
    cursor: 'pointer', marginTop: 8,
    animation: 'casavio-reviewBtnIn 350ms ease-out 80ms backwards',
    pointerEvents: 'auto',
  },
  // Pre-camera "Opening camera…" loading overlay (Change 1).
  spinnerBig: {
    width: 42, height: 42, borderRadius: '50%',
    border: '3px solid rgba(255,255,255,0.25)',
    borderTopColor: '#fff',
    animation: 'casavio-spin 0.9s linear infinite',
  },
  openingText: {
    color: '#fff', fontSize: 14, fontWeight: 500,
    fontFamily: 'Calibri, sans-serif', letterSpacing: 0.3,
  },
  // X on the Camera Access Needed modal (Change 2).
  modalCloseX: {
    position: 'absolute', top: 10, right: 12,
    background: 'transparent', border: 'none', cursor: 'pointer',
    color: C.muted, fontSize: '20px', lineHeight: 1,
    padding: '4px 8px', borderRadius: '8px',
    fontFamily: 'Calibri, sans-serif',
  },
  // "Can't find the data plate" link (Change 4).
  skipPlateLink: {
    background: 'transparent', border: 'none',
    color: 'rgba(255,255,255,0.65)', fontSize: '12px',
    fontFamily: 'Calibri, sans-serif', cursor: 'pointer',
    textDecoration: 'underline', padding: '8px',
    marginTop: '2px',
  },
  // Skip-data-plate inline confirmation overlay (Change 4).
  skipConfirmOverlay: {
    position: 'absolute', inset: 0,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 20, zIndex: 25,
  },
  skipConfirmCard: {
    background: C.card, borderRadius: 14,
    padding: '22px 22px 18px', maxWidth: 320, width: '100%',
    boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
  },
  skipConfirmTitle: {
    fontFamily: f, fontSize: 17, fontWeight: 700,
    color: C.brown, marginBottom: 8,
  },
  skipConfirmBody: {
    fontFamily: 'Calibri, sans-serif', fontSize: 13,
    color: C.brown, lineHeight: 1.5, marginBottom: 14,
  },
  skipConfirmRow: {
    display: 'flex', gap: 10, justifyContent: 'flex-end',
  },
  skipConfirmCancel: {
    background: 'transparent', color: C.muted,
    border: `1px solid ${C.border}`, borderRadius: 10,
    padding: '9px 16px', fontSize: 13,
    fontFamily: 'Calibri, sans-serif', cursor: 'pointer',
  },
  skipConfirmPrimary: {
    background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
    color: C.card, border: 'none', borderRadius: 10,
    padding: '9px 18px', fontSize: 13, fontWeight: 700,
    fontFamily: f, letterSpacing: 1, textTransform: 'uppercase',
    cursor: 'pointer',
  },
  recoveryPanel: {
    background: '#FFF8E7', border: `1px solid ${C.amber}55`,
    borderRadius: '14px', padding: '16px 18px', marginBottom: '16px',
  },
  recoveryTitle: {
    fontSize: '15px', fontWeight: '700', color: C.brown,
    fontFamily: f, marginBottom: '10px',
  },
  recoveryPhoto: {
    width: '100%', height: '100px', objectFit: 'cover',
    borderRadius: '8px', marginBottom: '12px', display: 'block',
  },
  recoveryOptions: {
    display: 'flex', flexDirection: 'column', gap: '8px',
  },
  recoveryOption: {
    display: 'flex', alignItems: 'flex-start', gap: '10px',
    fontSize: '13px', color: C.brown, fontFamily: 'Calibri, sans-serif',
  },
  recoveryNum: {
    flexShrink: 0, width: 20, height: 20, borderRadius: '50%',
    background: C.amber, color: '#fff', fontSize: '11px', fontWeight: '700',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: f,
  },
  retakeBtn: {
    marginTop: '12px', background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
    border: 'none', borderRadius: '8px', padding: '10px 18px',
    fontSize: '13px', fontWeight: '700', color: C.card,
    cursor: 'pointer', fontFamily: f,
  },
  photoPreserved: {
    marginTop: '10px', fontSize: '12px', color: C.success,
    fontFamily: 'Calibri, sans-serif', display: 'flex',
    alignItems: 'center', gap: '6px',
  },
}

export default function AddAppliance({ userId, propertyId, homeId, onClose, onSaved, initialType }) {
  // When initialType is provided (e.g. tapped a ghost tile in Standard Appliances prompt),
  // the type-grid on Step 1 is hidden and the bottom button switches to "Type manually →"
  // (Step 1 still shows the scan UI first, since scanning is the recommended path).
  const typeLocked = !!(initialType && TYPES.some(t => t.id === initialType))
  const [step, setStep] = useState(1)
  const [focus, setFocus] = useState('')
  const [loading, setLoading] = useState(false)
  const [scanning, setScanning] = useState(false)
  const [scanResult, setScanResult] = useState(null)

  // Session B/C two-photo flow with deliberate shutter capture.
  //   scanPhase:
  //     'photo1'             — shutter visible, instruction for photo 1
  //     'photo2'             — shutter visible, brand-aware instruction; photo 1
  //                            background identify runs in parallel
  //     'photo2_narrating'   — photo 2 still on screen, AI findings narrate in
  //                            progressively, "Tap to review →" when complete
  const [scanPhase, setScanPhase] = useState('photo1')
  const [photo2Entry, setPhoto2Entry] = useState(null)
  const [photo1ThumbUrl, setPhoto1ThumbUrl] = useState(null)
  const [retakeMenuOpen, setRetakeMenuOpen] = useState(false)
  // Photo 1 identify runs in the background after we optimistically advance to
  // Photo 2. The pill is shown while in-flight; the token guards against late
  // writes from a stale identify call clobbering newer state.
  const [backgroundIdentifying, setBackgroundIdentifying] = useState(false)
  const photo1IdentifyTokenRef = useRef(0)
  const photo1ExtractedRef = useRef(null)

  // Photo 2 narration: after the shutter, we display the captured photo and
  // progressively narrate the AI findings line-by-line. The narration is
  // client-side faked progressive rendering — v11 still returns one JSON blob.
  const [photo2DataUrl, setPhoto2DataUrl] = useState(null)
  const [narrationLines, setNarrationLines] = useState([])
  const [showReviewBtn, setShowReviewBtn] = useState(false)
  const [extractionFailed, setExtractionFailed] = useState(false)
  const narrationTimersRef = useRef([])
  const photo2SessionTokenRef = useRef(0)

  // "Can't find the data plate" skip flow: user explicitly opts out of capturing
  // a data plate. Sets data_plate_unavailable on the saved asset; Appliance
  // Details surfaces a small informational note.
  const [dataPlateUnavailable, setDataPlateUnavailable] = useState(false)
  const [skipConfirmOpen, setSkipConfirmOpen] = useState(false)

  const [alert, setAlert] = useState(null)
  const [showCamera, setShowCamera] = useState(false)
  const [cameraPermissionDenied, setCameraPermissionDenied] = useState(false)
  // Pre-camera loading state: 'opening' while we hand off to getUserMedia,
  // 'ready' once we have a stream, 'failed' if any failure path triggers.
  // The State 1 chooser only renders when 'failed' (or after the user
  // explicitly stops the camera mid-flow).
  const [cameraInit, setCameraInit] = useState('opening')
  const [torchSupported, setTorchSupported] = useState(false)
  const [torchOn, setTorchOn] = useState(false)
  // scanFailed retained for legacy fallback UI (manual scan failure surface);
  // the new shutter flow never sets it to true, so those JSX branches are inert.
  const [scanFailed, setScanFailed] = useState(false)

  // Detected once on mount — stable for the session
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

  const fileInputRef = useRef(null)
  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const photoCanvasRef = useRef(null) // shared canvas for shutter capture
  const trackRef = useRef(null)

  // Dev-mode scan telemetry — gated by NODE_ENV, never runs in production
  const DEV = process.env.NODE_ENV === 'development'
  const scanExtractedRef = useRef(null) // last AI extraction, for accepted/edited comparison at save
  const hasSaved = useRef(false)        // set true on successful save, read in cleanup for dismiss detection
  const torchOnRef = useRef(false)      // mirror of torchOn state, accessible in interval closures

  // Hinted re-scan (Phase 1)
  const [hintScanning, setHintScanning] = useState(false)
  const [aiSuggested, setAiSuggested] = useState({})  // field names AI filled in; cleared when user edits
  const [hintSuccess, setHintSuccess] = useState(false)
  const [makeMismatch, setMakeMismatch] = useState(null) // detected make differs from what user typed
  const lastScanBase64Ref = useRef(null) // base64 of last scan image (camera or upload)
  const hintInFlightRef = useRef(false)  // mutex — ref not state to avoid stale closure in debounce
  const hintDebounceRef = useRef(null)

  // Quick Identify (identify-appliance edge function) — primary path
  const [quickIdResult, setQuickIdResult] = useState(null)         // { type, make, approximate_age, confidence, visual_notes, plate_likely_location }
  const [quickIdentifying, setQuickIdentifying] = useState(false)
  const [quickIdPhotoPreview, setQuickIdPhotoPreview] = useState(null) // data URL for on-screen display
  const [quickIdConfirmNeeded, setQuickIdConfirmNeeded] = useState(false) // medium-confidence Yes/No prompt
  const [quickIdLowConf, setQuickIdLowConf] = useState(false)      // low-confidence retry/manual panel
  const [plateHint, setPlateHint] = useState(null)                 // shown as top banner inside camera overlay
  const quickIdCameraInputRef = useRef(null)   // capture="environment" — forces camera
  const quickIdGalleryInputRef = useRef(null)  // no capture — opens photo library

  const [form, setForm] = useState({
    type: '', name: '', make: '', model: '',
    serial: '', manufactured: '', location: '', nickname: '', notes: '',
  })
  const [photoFile, setPhotoFile] = useState(null)
  const [photoPreview, setPhotoPreview] = useState(null)
  const photoInputRef = useRef(null)

  // Hinted re-scan: fire 800ms after user stops typing in make field (step 2, scan failed)
  useEffect(() => {
    if (step !== 2) return
    if (!form.make.trim()) return
    if (!lastScanBase64Ref.current) return
    if (scanResult) return // scan already succeeded — no need to re-scan
    if (hintDebounceRef.current) clearTimeout(hintDebounceRef.current)
    hintDebounceRef.current = setTimeout(() => {
      triggerHintedRescan(form.make.trim())
    }, 800)
    return () => { if (hintDebounceRef.current) clearTimeout(hintDebounceRef.current) }
  }, [form.make, step]) // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup camera on unmount; log dismiss event in dev if user had a scan result but didn't save
  useEffect(() => {
    return () => {
      if (DEV && scanExtractedRef.current && !hasSaved.current) {
        console.log('%c[Scan] DISMISSED — closed without saving (had scan result)', 'color:#DC2626;font-weight:bold')
        console.log('  extracted was:', scanExtractedRef.current)
      }
      // Tour signal: the appliance flow closed (saved or cancelled). The welcome
      // tour's step 3 waits on this to advance/skip. See src/lib/appEvents.js.
      emitTourEvent(TOUR_EVENT_TYPES.APPLIANCE_FLOW_CLOSED, { saved: hasSaved.current })
      stopCamera()
    }
  }, [])

  const set = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }))
    setAlert(null)
    if (aiSuggested[field]) setAiSuggested(prev => { const n = { ...prev }; delete n[field]; return n })
  }

  const selectType = (id) => {
    const typeLabel = TYPES.find(t => t.id === id)?.label || id
    setForm(f => ({ ...f, type: id, name: typeLabel }))
  }

  // Pre-select a type on mount when caller passes initialType (e.g. from the
  // Standard Appliances prompt). Only fires once — won't fight the user later.
  useEffect(() => {
    if (initialType && TYPES.some(t => t.id === initialType)) {
      selectType(initialType)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Direct-to-scan: tapping "+ Add appliance" (or a Standard Appliances tile)
  // goes straight to the camera scan view. The State 1 chooser stays in the
  // JSX as a fallback for when camera launch fails (denied permission, no
  // device, etc.). Only fires on first mount.
  useEffect(() => {
    if (step !== 1) return
    if (showCamera) return
    if (scanResult || scanFailed) return
    if (quickIdResult || quickIdConfirmNeeded || quickIdLowConf) return
    if (cameraPermissionDenied) return
    startCamera()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── LIVE CAMERA ────────────────────────────────────────────────
  const startCamera = async () => {
    setCameraInit('opening')
    // Check if camera API is available at all
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setAlert('Camera not available. Your browser may not support this feature — try using the photo upload option instead.')
      setCameraInit('failed')
      return
    }

    // Check current permission state if the API supports it
    if (navigator.permissions) {
      try {
        const permStatus = await navigator.permissions.query({ name: 'camera' })
        if (permStatus.state === 'denied') {
          // Show guided permission modal instead of silent error
          setCameraPermissionDenied(true)
          setCameraInit('failed')
          return
        }
      } catch (e) {
        // permissions.query not supported — proceed and let getUserMedia handle it
      }
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
      })
      streamRef.current = stream
      setScanFailed(false)

      // Detect torch capability on this device/track
      const track = stream.getVideoTracks()[0]
      trackRef.current = track
      const capabilities = track.getCapabilities ? track.getCapabilities() : {}
      setTorchSupported(!!capabilities.torch)
      setTorchOn(false)

      setCameraPermissionDenied(false)
      setShowCamera(true)
      setCameraInit('ready')
      // Reset to fresh photo-1 capture state every time the camera opens.
      setScanPhase('photo1')
      setPhoto2Entry(null)
      setPhoto1ThumbUrl(null)
      setRetakeMenuOpen(false)
      setDataPlateUnavailable(false)
      setSkipConfirmOpen(false)
      photo1ExtractedRef.current = null
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream
          videoRef.current.play()
        }
      }, 100)
    } catch (err) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setCameraPermissionDenied(true)
      } else if (err.name === 'NotFoundError') {
        setAlert('No camera found on this device. Please use the photo upload option.')
      } else if (err.name === 'NotReadableError') {
        setAlert('Camera is in use by another app. Please close other apps and try again.')
      } else {
        setAlert('Could not access camera. Please use the photo upload option instead.')
      }
      setCameraInit('failed')
    }
  }

  const clearNarrationTimers = () => {
    for (const t of narrationTimersRef.current) clearTimeout(t)
    narrationTimersRef.current = []
  }

  const stopCamera = () => {
    // Invalidate any in-flight background work so late writes don't reach
    // into a fresh camera session.
    photo1IdentifyTokenRef.current++
    photo2SessionTokenRef.current++
    clearNarrationTimers()
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
      streamRef.current = null
    }
    trackRef.current = null
    setShowCamera(false)
    setTorchOn(false)
    setTorchSupported(false)
    torchOnRef.current = false
    // Reset two-photo scan state so a fresh scan starts clean next time.
    setScanPhase('photo1')
    setPhoto2Entry(null)
    setPhoto1ThumbUrl(null)
    setRetakeMenuOpen(false)
    setBackgroundIdentifying(false)
    setPhoto2DataUrl(null)
    setNarrationLines([])
    setShowReviewBtn(false)
    setExtractionFailed(false)
    photo1ExtractedRef.current = null
  }

  const toggleTorch = async () => {
    if (!trackRef.current || !torchSupported) return
    const next = !torchOn
    try {
      await trackRef.current.applyConstraints({ advanced: [{ torch: next }] })
      setTorchOn(next)
      torchOnRef.current = next
    } catch {
      // torch toggle silently unsupported on this device at runtime
    }
  }

  // Build a File + dataUrl from a base64 frame and stage it as the
  // appliance's primary photo. Used by both photo 1 and photo 2 capture
  // (only photo 1's base64 actually becomes the staged photoFile — photo 2
  // is the data plate, which the user does not necessarily want as the
  // gallery photo).
  function stageFrameAsPhoto(base64, filenamePrefix) {
    try {
      const byteString = atob(base64)
      const ab = new ArrayBuffer(byteString.length)
      const ia = new Uint8Array(ab)
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i)
      const blob = new Blob([ab], { type: 'image/jpeg' })
      const file = new File([blob], `${filenamePrefix}-${Date.now()}.jpg`, { type: 'image/jpeg' })
      setPhotoFile(file)
      setPhotoPreview(`data:image/jpeg;base64,${base64}`)
    } catch { /* non-critical */ }
  }

  function parseExtraction(text) {
    if (!text) return null
    const clean = text.replace(/```json/gi, '').replace(/```/g, '').trim()
    const jsonMatch = clean.match(/\{[\s\S]*\}/)
    if (!jsonMatch) return null
    try { return JSON.parse(jsonMatch[0]) } catch { return null }
  }

  // Session B helper: merge photo 1's extraction with photo 2's, preferring
  // non-null values from photo 2 (the more targeted shot) and unioning the
  // features array case-insensitively. Photo 1 establishes the broad
  // identification; photo 2 fills in the gaps from the data plate / branding.
  function mergeExtracted(a, b) {
    const out = { ...(a || {}), ...(b || {}) }
    // For each top-level scalar in `out`, prefer photo 2's non-null over photo 1's value.
    // The ...(b || {}) spread already handles this for scalar keys present in b.
    // But b may have explicit null for a field photo 1 had — restore from a in that case.
    if (a) {
      for (const k of Object.keys(a)) {
        if (out[k] == null && a[k] != null) out[k] = a[k]
      }
    }
    // Specifications: shallow merge, photo 2's non-null wins.
    const aSpecs = (a && a.specifications) || {}
    const bSpecs = (b && b.specifications) || {}
    const mergedSpecs = { ...aSpecs }
    for (const [k, v] of Object.entries(bSpecs)) {
      if (v != null) mergedSpecs[k] = v
    }
    out.specifications = mergedSpecs
    // Features: union with case-insensitive dedupe.
    const aF = Array.isArray(a?.features) ? a.features : []
    const bF = Array.isArray(b?.features) ? b.features : []
    const seen = new Set()
    const merged = []
    for (const v of [...aF, ...bF]) {
      if (typeof v !== 'string') continue
      const k = v.trim().toLowerCase()
      if (!k || seen.has(k)) continue
      seen.add(k)
      merged.push(v.trim())
    }
    out.features = merged
    return out
  }

  const captureFrameBase64 = () => {
    const video = videoRef.current
    if (!video || !video.videoWidth) return null
    const canvas = document.createElement('canvas')
    const maxDim = 1024
    let w = video.videoWidth, h = video.videoHeight
    if (w > maxDim || h > maxDim) {
      if (w > h) { h = Math.round(h * maxDim / w); w = maxDim }
      else { w = Math.round(w * maxDim / h); h = maxDim }
    }
    canvas.width = w; canvas.height = h
    canvas.getContext('2d').drawImage(video, 0, 0, w, h)
    return canvas.toDataURL('image/jpeg', 0.85).split(',')[1]
  }

  // ── Shutter capture flow (replaces the old auto-scan loop) ───────────
  // Photo 1: user taps shutter → AI identifies brand/type → look up
  //          brand-aware Photo 2 instructions (or generic fallback) →
  //          transition to Photo 2.
  // Photo 2: user taps shutter → identify-appliance v11 called with
  //          spec_extraction_hints + photo_2_role → merge with Photo 1 →
  //          advance to confirm/low-conf/step-2 based on confidence.

  const handleShutterTap = () => {
    if (scanPhase === 'photo1') return capturePhoto1()
    if (scanPhase === 'photo2') return capturePhoto2()
  }

  const safeVibrate = (ms) => {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      try { navigator.vibrate(ms) } catch { /* unsupported */ }
    }
  }

  async function capturePhoto1() {
    const base64 = captureFrameBase64()
    if (!base64) return
    safeVibrate(15)

    const thumbUrl = `data:image/jpeg;base64,${base64}`
    setPhoto1ThumbUrl(thumbUrl)
    lastScanBase64Ref.current = base64
    stageFrameAsPhoto(base64, 'scan-photo1')

    // OPTIMISTIC: jump straight to Photo 2 with the generic instruction.
    // The user is already standing in front of the appliance — they shouldn't
    // wait for a network round-trip to take the next photo. If a brand match
    // comes back from the background identify, we swap the instruction in
    // place with a smooth fade-in animation.
    const genericEntry = await getGenericInstructions('appliance')
    setPhoto2Entry(genericEntry)
    setScanPhase('photo2')
    setBackgroundIdentifying(true)

    // Background work — does not block the UI. Late writes are gated by a
    // token so a stale call can't clobber a fresh camera session.
    const myToken = ++photo1IdentifyTokenRef.current
    ;(async () => {
      let extracted = null
      try {
        const { data, error: fnError } = await supabase.functions.invoke('identify-appliance', {
          body: { imageBase64: base64, mediaType: 'image/jpeg' },
        })
        if (fnError) throw new Error(fnError.message)
        extracted = parseExtraction(data?.text || '')
      } catch (e) {
        if (DEV) console.warn('[Scan] photo 1 background identify failed', e?.message || e)
      }
      if (DEV) console.log('[Scan] photo 1 extracted (background):', extracted)

      if (myToken !== photo1IdentifyTokenRef.current) return

      // photo1ExtractedRef feeds the v11 hint when capturePhoto2 fires later.
      // If the user already tapped Photo 2 (scanPhase moved past 'photo2'),
      // setting this is moot but harmless — capturePhoto2 already read its
      // value (null) at the time it ran.
      photo1ExtractedRef.current = extracted
      scanExtractedRef.current = extracted
      if (extracted) {
        setQuickIdResult(extracted)
        setQuickIdPhotoPreview(thumbUrl)
      }

      // Brand-aware swap. Only attempt if we actually got a make+type.
      if (extracted && extracted.type) {
        try {
          // Brand-aware lookup candidate order:
          //   1. The user's pre-selected type (form.type, set from initialType
          //      when the user came in via a Standard Appliances tile). Honors
          //      the user's intent first.
          //   2. AI-derived candidates (subtype heuristics from visual_notes).
          // lookupBestInstructions tries each in order and returns the first hit.
          const aiCandidates = applianceTypeCandidates(extracted)
          const userType = form.type ? [form.type] : []
          const candidates = [
            ...userType,
            ...aiCandidates.filter(c => !userType.includes(c)),
          ]
          const brand = normalizeBrand(extracted.make || '')
          if (brand) {
            const matched = await lookupBestInstructions(candidates, brand)
            if (myToken === photo1IdentifyTokenRef.current && matched) {
              setPhoto2Entry(matched)
            }
          }
        } catch (e) {
          if (DEV) console.warn('[Scan] data_plate_locations lookup failed', e?.message || e)
        }
      }

      if (myToken === photo1IdentifyTokenRef.current) setBackgroundIdentifying(false)
    })()
  }

  // Order in which AI findings are narrated to the user. The display labels
  // match the JSON keys returned by the Edge Function.
  // eslint-disable-next-line no-restricted-syntax
  // (intentionally module-level-feeling const inside component — closure is fine)
  const NARRATION_FIELDS = [
    { id: 'make',         label: 'make' },
    { id: 'model',        label: 'model' },
    { id: 'serial',       label: 'serial' },
    { id: 'manufactured', label: 'manufactured' },
    { id: 'type',         label: 'type' },
  ]
  const REVEAL_DELAY_MS = 450
  const GAP_FILL_MS = 4000
  const REVIEW_BTN_DELAY_MS = 300

  function narrationLineFor(field, result) {
    const raw = result ? result[field.id] : null
    const val = raw == null || raw === '' ? null : String(raw)
    return {
      id: field.id,
      kind: val ? 'found' : 'not_visible',
      label: field.label,
      value: val,
    }
  }

  function scheduleNarrationReveal(myToken, result) {
    NARRATION_FIELDS.forEach((field, i) => {
      const t = setTimeout(() => {
        if (myToken !== photo2SessionTokenRef.current) return
        setNarrationLines(prev => [...prev, narrationLineFor(field, result)])
        if (i === NARRATION_FIELDS.length - 1) {
          const t2 = setTimeout(() => {
            if (myToken === photo2SessionTokenRef.current) setShowReviewBtn(true)
          }, REVIEW_BTN_DELAY_MS)
          narrationTimersRef.current.push(t2)
        }
      }, i * REVEAL_DELAY_MS)
      narrationTimersRef.current.push(t)
    })
  }

  function handleReviewTap() {
    const merged = scanExtractedRef.current
    const conf = (merged?.confidence || '').toLowerCase()
    const typeId = merged ? matchQuickIdType(merged.type) : null
    stopCamera()
    if (extractionFailed || !merged || !typeId || conf === 'low') {
      setQuickIdLowConf(true)
    } else if (conf === 'medium') {
      setQuickIdConfirmNeeded(true)
    } else {
      setStep(2)
    }
  }

  async function capturePhoto2() {
    const base64 = captureFrameBase64()
    if (!base64) return
    safeVibrate(15)

    // Cancel any prior session's timers and bump session token so late writes
    // from a previous narration can't bleed in.
    clearNarrationTimers()
    const myToken = ++photo2SessionTokenRef.current

    setPhoto2DataUrl(`data:image/jpeg;base64,${base64}`)
    setNarrationLines([])
    setShowReviewBtn(false)
    setExtractionFailed(false)
    setScanPhase('photo2_narrating')

    // 4s gap-fill: if the AI call is still pending, show "Looking deeper…" so
    // the screen isn't blank during a long wait.
    const gapFillTimer = setTimeout(() => {
      if (myToken !== photo2SessionTokenRef.current) return
      setNarrationLines(prev => prev.length > 0 ? prev : [{
        id: '__gap__', kind: 'gap_fill', label: 'looking deeper…', value: null,
      }])
    }, GAP_FILL_MS)
    narrationTimersRef.current.push(gapFillTimer)

    // Build request with hints + photo_2_role + brand/type carry-over.
    const requestBody = { imageBase64: base64, mediaType: 'image/jpeg' }
    const p1Make = photo1ExtractedRef.current?.make || null
    const p1Type = photo1ExtractedRef.current?.type || null
    if (p1Make || p1Type) {
      requestBody.hint = {
        ...(p1Make ? { make: p1Make } : {}),
        ...(p1Type ? { type: p1Type } : {}),
      }
    }
    if (Array.isArray(photo2Entry?.spec_extraction_hints) && photo2Entry.spec_extraction_hints.length > 0) {
      requestBody.spec_extraction_hints = photo2Entry.spec_extraction_hints
    }
    const role = photo2Entry?.photos?.[1]?.role
    if (role) requestBody.photo_2_role = role

    let merged = photo1ExtractedRef.current || null
    let succeeded = false
    try {
      const { data, error: fnError } = await supabase.functions.invoke('identify-appliance', {
        body: requestBody,
      })
      if (fnError) throw new Error(fnError.message)
      const photo2Extracted = parseExtraction(data?.text || '')
      if (DEV) console.log('[Scan] photo 2 extracted:', photo2Extracted)
      if (photo2Extracted) {
        merged = mergeExtracted(photo1ExtractedRef.current, photo2Extracted)
        succeeded = true
      } else if (photo1ExtractedRef.current) {
        succeeded = true
      }
    } catch (e) {
      if (DEV) console.warn('[Scan] photo 2 identify failed', e?.message || e)
    }

    if (myToken !== photo2SessionTokenRef.current) return

    clearTimeout(gapFillTimer)
    scanExtractedRef.current = merged
    setScanResult(merged)
    if (merged) {
      const typeId = matchQuickIdType(merged.type)
      if (typeId) applyQuickIdToForm(merged, typeId)
    }

    if (!succeeded || !merged) {
      // Failure path — single error line + manual-continue button.
      setExtractionFailed(true)
      setNarrationLines([{
        id: '__fail__', kind: 'failed',
        label: "Couldn't extract details from this photo", value: null,
      }])
      const tFail = setTimeout(() => {
        if (myToken === photo2SessionTokenRef.current) setShowReviewBtn(true)
      }, REVIEW_BTN_DELAY_MS)
      narrationTimersRef.current.push(tFail)
      return
    }

    // Success path: clear any gap-fill line, then animate findings line by line.
    setNarrationLines([])
    scheduleNarrationReveal(myToken, merged)
  }

  function retakePhoto1() {
    // Invalidate any in-flight async work so its late writes don't re-populate
    // stale state into the fresh photo 1 attempt.
    photo1IdentifyTokenRef.current++
    photo2SessionTokenRef.current++
    clearNarrationTimers()
    photo1ExtractedRef.current = null
    scanExtractedRef.current = null
    setQuickIdResult(null)
    setQuickIdPhotoPreview(null)
    setPhoto1ThumbUrl(null)
    setPhoto2Entry(null)
    setRetakeMenuOpen(false)
    setBackgroundIdentifying(false)
    setPhoto2DataUrl(null)
    setNarrationLines([])
    setShowReviewBtn(false)
    setExtractionFailed(false)
    setScanPhase('photo1')
  }

  // Retake just Photo 2 — preserve Photo 1, its extraction, and the brand-
  // aware photo2Entry. Reverts scanResult to photo1-only data so the user
  // doesn't see stale photo 2 findings persist while they reshoot.
  function retakePhoto2() {
    photo2SessionTokenRef.current++
    clearNarrationTimers()
    setPhoto2DataUrl(null)
    setNarrationLines([])
    setShowReviewBtn(false)
    setExtractionFailed(false)
    // Roll the saved scan result back to what photo 1 produced.
    const p1 = photo1ExtractedRef.current
    scanExtractedRef.current = p1
    setScanResult(p1 || null)
    setScanPhase('photo2')
  }

  // Skip data plate entirely — user couldn't find / read it. Persists the
  // intent on the asset (data_plate_unavailable=true) and advances to the
  // manual-entry step with whatever Photo 1 extracted.
  function confirmSkipDataPlate() {
    photo2SessionTokenRef.current++
    clearNarrationTimers()
    setSkipConfirmOpen(false)
    setDataPlateUnavailable(true)
    const p1 = photo1ExtractedRef.current
    if (p1) {
      scanExtractedRef.current = p1
      setScanResult(p1)
      const typeId = matchQuickIdType(p1.type)
      if (typeId) applyQuickIdToForm(p1, typeId)
    }
    stopCamera()
    setStep(2)
  }

  // ── PHOTO UPLOAD FALLBACK ───────────────────────────────────────
  const handleScanClick = () => fileInputRef.current?.click()

  const handleImageCapture = async (e) => {
    let file = e.target.files?.[0]
    if (e.target) e.target.value = ''
    if (!file) return
    setScanning(true)
    setScanResult(null)
    setAlert(null)

    try {
      file = await convertHeicIfNeeded(file)
    } catch (err) {
      setScanning(false)
      setAlert(err.message || 'Could not process this photo. Please try another.')
      return
    }

    let imgDims = 'unknown'

    try {
      const compressed = await new Promise((resolve, reject) => {
        const img = new Image()
        const objectUrl = URL.createObjectURL(file)
        img.onload = () => {
          imgDims = `${img.naturalWidth}×${img.naturalHeight}`
          URL.revokeObjectURL(objectUrl)
          const canvas = document.createElement('canvas')
          const maxDim = 1024
          let { width, height } = img
          if (width > maxDim || height > maxDim) {
            if (width > height) { height = Math.round(height * maxDim / width); width = maxDim }
            else { width = Math.round(width * maxDim / height); height = maxDim }
          }
          canvas.width = width; canvas.height = height
          canvas.getContext('2d').drawImage(img, 0, 0, width, height)
          canvas.toBlob(
            (blob) => {
              const reader = new FileReader()
              reader.onload = () => resolve(reader.result.split(',')[1])
              reader.onerror = reject
              reader.readAsDataURL(blob)
            },
            'image/jpeg', 0.85
          )
        }
        img.onerror = reject
        img.src = objectUrl
      })

      lastScanBase64Ref.current = compressed

      const { data, error: fnError } = await supabase.functions.invoke('identify-appliance', {
        body: { imageBase64: compressed, mediaType: 'image/jpeg' },
      })

      if (fnError) throw new Error(fnError.message)

      const text = data?.text || ''
      const clean = text.replace(/```json/gi, '').replace(/```/g, '').trim()
      const jsonMatch = clean.match(/\{[\s\S]*\}/)
      if (!jsonMatch) throw new Error('Could not parse response')

      const extracted = JSON.parse(jsonMatch[0])

      if (DEV) {
        console.group(`%c[Scan] upload SUCCESS | ${imgDims} | ${(file.size / 1024).toFixed(1)} KB`, 'color:#16A34A;font-weight:bold')
        console.log('file:', file.name)
        console.log('torch: N/A (photo upload)')
        console.log('raw:', text)
        console.log('extracted:', extracted)
        console.groupEnd()
      }
      scanExtractedRef.current = extracted

      // Build a File from the compressed bytes so the appliance-photo upload path picks it up
      const photoDataUrl = `data:image/jpeg;base64,${compressed}`
      try {
        const byteString = atob(compressed)
        const ab = new ArrayBuffer(byteString.length)
        const ia = new Uint8Array(ab)
        for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i)
        const photoBlob = new Blob([ab], { type: 'image/jpeg' })
        const photoFileObj = new File([photoBlob], `scan-${Date.now()}.jpg`, { type: 'image/jpeg' })
        setPhotoFile(photoFileObj)
        setPhotoPreview(photoDataUrl)
      } catch { /* non-critical */ }

      setQuickIdResult(extracted)
      setQuickIdPhotoPreview(photoDataUrl)

      const typeId = matchQuickIdType(extracted.type)
      applyQuickIdToForm(extracted, typeId)

      const conf = (extracted.confidence || '').toLowerCase()

      if (!typeId || conf === 'low') {
        setQuickIdLowConf(true)
      } else if (conf === 'medium') {
        setQuickIdConfirmNeeded(true)
      } else {
        // High confidence — pre-fill, mark scanResult, advance
        setScanResult(extracted)
        setStep(2)
      }
    } catch (err) {
      if (DEV) {
        console.group(`%c[Scan] upload FAILED | ${imgDims} | ${(file.size / 1024).toFixed(1)} KB`, 'color:#DC2626;font-weight:bold')
        console.log('file:', file.name)
        console.log('torch: N/A (photo upload)')
        console.log('error:', err.message)
        console.groupEnd()
      }
      setAlert('Could not read the data plate. Please enter details manually.')
    } finally {
      setScanning(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }
  // ────────────────────────────────────────────────────────────────

  const triggerHintedRescan = async (makeHint) => {
    if (hintInFlightRef.current) return
    if (!lastScanBase64Ref.current) return
    hintInFlightRef.current = true
    setHintScanning(true)
    setHintSuccess(false)
    setMakeMismatch(null)
    try {
      const { data, error: fnError } = await supabase.functions.invoke('identify-appliance', {
        body: {
          imageBase64: lastScanBase64Ref.current,
          mediaType: 'image/jpeg',
          hint: { make: makeHint, ...(form.type ? { type: form.type } : {}) },
        },
      })
      if (fnError) throw new Error(fnError.message)
      const text = data?.text || ''
      const clean = text.replace(/```json/gi, '').replace(/```/g, '').trim()
      const jsonMatch = clean.match(/\{[\s\S]*\}/)
      if (!jsonMatch) return
      const extracted = JSON.parse(jsonMatch[0])
      const updates = {}
      const newSuggested = {}
      for (const field of ['model', 'serial', 'manufactured']) {
        if (extracted[field] && !form[field]?.trim()) {
          updates[field] = extracted[field]
          newSuggested[field] = true
        }
      }
      if (extracted.make && extracted.make.toLowerCase() !== makeHint.toLowerCase()) {
        setMakeMismatch(extracted.make)
      }
      if (Object.keys(updates).length > 0) {
        setForm(prev => ({ ...prev, ...updates }))
        setAiSuggested(prev => ({ ...prev, ...newSuggested }))
        setHintSuccess(true)
      }
      if (DEV) {
        console.group('%c[Scan] hint re-scan', 'color:#B8721F;font-weight:bold')
        console.log('hint:', makeHint, '| raw:', text)
        console.log('extracted:', extracted, '| updates:', updates)
        console.groupEnd()
      }
    } catch (err) {
      if (DEV) console.log('[Scan] hint re-scan failed:', err.message)
    } finally {
      hintInFlightRef.current = false
      setHintScanning(false)
    }
  }

  // ── QUICK IDENTIFY (identify-appliance) ───────────────────────────
  // Map free-form AI type string ("Dishwasher", "Refrigerator", etc.) to a TYPES id.
  const summarizeSpecs = (specs) => {
    if (!specs) return null
    const parts = []
    if (specs.capacity_gallons) parts.push(`${specs.capacity_gallons} gal`)
    if (specs.capacity_cu_ft) parts.push(`${specs.capacity_cu_ft} cu ft`)
    if (specs.btu_per_hour) parts.push(`${Number(specs.btu_per_hour).toLocaleString()} BTU/hr`)
    if (specs.tonnage) parts.push(`${specs.tonnage} ton`)
    if (specs.watts) parts.push(`${specs.watts}W`)
    if (specs.fuel_type) parts.push(specs.fuel_type)
    if (specs.venting) parts.push(`${specs.venting} vent`)
    if (specs.country_of_origin) parts.push(`made in ${specs.country_of_origin}`)
    if (specs.oem_manufacturer) parts.push(`OEM: ${specs.oem_manufacturer}`)
    if (specs.other) parts.push(specs.other)
    return parts.length ? parts.join(' · ') : null
  }

  const matchQuickIdType = (aiType) => {
    if (!aiType) return null
    const lower = aiType.toLowerCase().trim()
    const direct = TYPES.find(t => t.id === lower)
    if (direct) return direct.id
    const byLabel = TYPES.find(t => t.label.toLowerCase().includes(lower) || lower.includes(t.label.toLowerCase()))
    if (byLabel) return byLabel.id
    if (lower.includes('fridge') || lower.includes('refriger')) return 'refrigerator'
    if (lower.includes('hvac') || lower.includes('furnace') || lower.includes('air condition')) return 'hvac'
    if (lower.includes('water heater')) return 'water_heater'
    if (lower.includes('washer') || lower.includes('washing')) return 'washer'
    if (lower.includes('dryer')) return 'dryer'
    if (lower.includes('dishwash')) return 'dishwasher'
    if (lower.includes('oven') || lower.includes('range') || lower.includes('stove')) return 'oven'
    if (lower.includes('microwave')) return 'microwave'
    if (lower.includes('disposal')) return 'garbage_disposal'
    if (lower.includes('smoke') || lower.includes('detector')) return 'smoke_detector'
    if (lower.includes('garage')) return 'garage_door'
    return null
  }

  const openQuickIdCameraPicker = () => quickIdCameraInputRef.current?.click()
  const openQuickIdGalleryPicker = () => quickIdGalleryInputRef.current?.click()

  const resetQuickIdState = () => {
    setQuickIdResult(null)
    setQuickIdPhotoPreview(null)
    setQuickIdConfirmNeeded(false)
    setQuickIdLowConf(false)
  }

  const handleQuickIdentifyCapture = async (e) => {
    let file = e.target.files?.[0]
    if (e.target) e.target.value = ''
    if (!file) return

    setQuickIdentifying(true)
    setQuickIdResult(null)
    setQuickIdConfirmNeeded(false)
    setQuickIdLowConf(false)
    setAlert(null)

    try {
      file = await convertHeicIfNeeded(file)
    } catch (err) {
      setQuickIdentifying(false)
      setAlert(err.message || 'Could not process this photo. Please try another.')
      return
    }

    let imgDims = 'unknown'

    try {
      const compressed = await new Promise((resolve, reject) => {
        const img = new Image()
        const objectUrl = URL.createObjectURL(file)
        img.onload = () => {
          imgDims = `${img.naturalWidth}×${img.naturalHeight}`
          URL.revokeObjectURL(objectUrl)
          const canvas = document.createElement('canvas')
          const maxDim = 1024
          let { width, height } = img
          if (width > maxDim || height > maxDim) {
            if (width > height) { height = Math.round(height * maxDim / width); width = maxDim }
            else { width = Math.round(width * maxDim / height); height = maxDim }
          }
          canvas.width = width; canvas.height = height
          canvas.getContext('2d').drawImage(img, 0, 0, width, height)
          canvas.toBlob(
            (blob) => {
              const reader = new FileReader()
              reader.onload = () => resolve(reader.result.split(',')[1])
              reader.onerror = reject
              reader.readAsDataURL(blob)
            },
            'image/jpeg', 0.85
          )
        }
        img.onerror = reject
        img.src = objectUrl
      })

      const photoDataUrl = `data:image/jpeg;base64,${compressed}`
      setQuickIdPhotoPreview(photoDataUrl)

      // Auto-save the captured photo to the appliance gallery — reuses the existing
      // photoFile upload path in handleSave (which also fires recordProvenance).
      try {
        const byteString = atob(compressed)
        const ab = new ArrayBuffer(byteString.length)
        const ia = new Uint8Array(ab)
        for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i)
        const photoBlob = new Blob([ab], { type: 'image/jpeg' })
        const photoFileObj = new File([photoBlob], `quickid-${Date.now()}.jpg`, { type: 'image/jpeg' })
        setPhotoFile(photoFileObj)
        setPhotoPreview(photoDataUrl)
      } catch { /* non-critical — AI flow proceeds even if photo handoff fails */ }

      const { data, error: fnError } = await supabase.functions.invoke('identify-appliance', {
        body: { imageBase64: compressed, mediaType: 'image/jpeg' },
      })
      if (fnError) throw new Error(fnError.message)

      const text = data?.text || ''
      const clean = text.replace(/```json/gi, '').replace(/```/g, '').trim()
      const jsonMatch = clean.match(/\{[\s\S]*\}/)
      if (!jsonMatch) throw new Error('Could not parse identification response')

      const result = JSON.parse(jsonMatch[0])

      if (DEV) {
        console.group(`%c[QuickId] result | ${imgDims} | ${(file.size / 1024).toFixed(1)} KB`, 'color:#B8721F;font-weight:bold')
        console.log('raw:', text)
        console.log('parsed:', result)
        console.groupEnd()
      }

      setQuickIdResult(result)

      const conf = (result.confidence || '').toLowerCase()
      const typeId = matchQuickIdType(result.type)

      if (!result.type || conf === 'low') {
        setQuickIdLowConf(true)
        return
      }

      if (conf === 'medium') {
        setQuickIdConfirmNeeded(true)
        return
      }

      // High confidence — pre-fill and advance
      applyQuickIdToForm(result, typeId)
      setStep(2)
    } catch (err) {
      if (DEV) console.log('[QuickId] failed:', err.message)
      setAlert('Could not identify the appliance. Try another angle or fill in details manually.')
      setQuickIdLowConf(true)
    } finally {
      setQuickIdentifying(false)
    }
  }

  const applyQuickIdToForm = (result, typeId) => {
    const matched = typeId || matchQuickIdType(result?.type)
    setForm(prev => ({
      ...prev,
      type: matched || prev.type,
      name: matched ? (TYPES.find(t => t.id === matched)?.label || prev.name) : prev.name,
      make: result?.make || prev.make,
      model: result?.model || prev.model,
      serial: result?.serial || prev.serial,
      manufactured: result?.manufactured || prev.manufactured,
    }))
    if (result?.plate_likely_location) setPlateHint(result.plate_likely_location)
  }

  const confirmQuickIdYes = () => {
    applyQuickIdToForm(quickIdResult, matchQuickIdType(quickIdResult?.type))
    setQuickIdConfirmNeeded(false)
    setStep(2)
  }

  const confirmQuickIdNo = () => {
    setQuickIdConfirmNeeded(false)
    // Advance without pre-filling type/make so user enters manually
    setStep(2)
  }

  const openDataPlateScanFromStep2 = () => {
    if (quickIdResult?.plate_likely_location) setPlateHint(quickIdResult.plate_likely_location)
    startCamera()
  }
  // ────────────────────────────────────────────────────────────────

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !file.type.startsWith('image/')) return

    let processedFile = file
    try {
      processedFile = await convertHeicIfNeeded(file)
    } catch (err) {
      setAlert(err.message || 'Could not process this photo. Please try another.')
      return
    }

    setPhotoFile(processedFile)
    const reader = new FileReader()
    reader.onloadend = () => setPhotoPreview(reader.result)
    reader.readAsDataURL(processedFile)
  }

  const clearPhoto = () => { setPhotoFile(null); setPhotoPreview(null) }

  const canProceed1 = !!form.type
  const canProceed2 = form.name.trim() && (form.make.trim() || scanFailed)

  // Silent-save-and-close: triggered by the top-right X on step 2. Per the
  // Casavio Data Preservation Principle, closing never destroys user work —
  // partial records become Incomplete tiles. No confirmation modal.
  const silentSaveAndClose = () => {
    const typeLabel = TYPES.find(t => t.id === form.type)?.label || 'Appliance'
    const overrides = {}
    if (!form.name.trim()) overrides.name = form.nickname.trim() || typeLabel
    if (!form.make.trim()) {
      const aiMake = scanExtractedRef.current?.make || quickIdResult?.make
      if (aiMake) overrides.make = aiMake
    }
    return performSave({ overrides, allowPartial: true })
  }

  const handleSave = () => performSave()

  const performSave = async ({ overrides = {}, allowPartial = false } = {}) => {
    // Effective values: overrides win when provided, else trimmed form values.
    const eff = {
      name: ((overrides.name ?? form.name) || '').trim(),
      nickname: ((overrides.nickname ?? form.nickname) || '').trim(),
      location: ((overrides.location ?? form.location) || '').trim(),
      type: overrides.type ?? form.type,
      make: ((overrides.make ?? form.make) || '').trim(),
      model: ((overrides.model ?? form.model) || '').trim(),
      serial: ((overrides.serial ?? form.serial) || '').trim(),
      manufactured: ((overrides.manufactured ?? form.manufactured) || '').trim(),
      notes: ((overrides.notes ?? form.notes) || '').trim(),
    }

    if (!allowPartial) {
      if (!canProceed2) return
    } else {
      // Partial saves still need at least a name; everything else can be filled
      // in later from the Incomplete tile.
      if (!eff.name) return
    }

    if (DEV) {
      const ex = scanExtractedRef.current
      if (ex) {
        const FIELDS = ['make', 'model', 'serial', 'manufactured']
        const changed = FIELDS.filter(f => (form[f]?.trim() || '') !== (ex[f] || ''))
        const verdict = changed.length === 0 ? 'ACCEPTED' : `EDITED [${changed.join(', ')}]`
        const color = changed.length === 0 ? '#16A34A' : '#B8721F'
        console.group(`%c[Scan] SAVE — ${verdict}`, `color:${color};font-weight:bold`)
        console.log('scanned:', ex)
        console.log('saved:  ', { make: form.make, model: form.model, serial: form.serial, manufactured: form.manufactured })
        if (changed.length > 0) changed.forEach(f => console.log(`  ${f}: "${ex[f] || ''}" → "${form[f]?.trim() || ''}"`))
        console.groupEnd()
      } else {
        console.log('%c[Scan] SAVE — MANUAL (no scan used)', 'color:#6B7280')
      }
    }

    setLoading(true)
    setAlert(null)

    // Always save specifications when AI returned a result, even if empty —
    // lets us distinguish "AI returned nothing" from "AI returned data we dropped".
    let specsToSave = null
    if (quickIdResult) {
      specsToSave = { ...(quickIdResult.specifications || {}) }
      if (quickIdResult.approximate_age && !specsToSave.approximate_age) {
        specsToSave.approximate_age = quickIdResult.approximate_age
      }
      if (quickIdResult.oem_manufacturer && !specsToSave.oem_manufacturer) {
        specsToSave.oem_manufacturer = quickIdResult.oem_manufacturer
      }
    }

    if (DEV) {
      console.group('%c[QuickId] save snapshot', 'color:#B8721F;font-weight:bold')
      console.log('result:', quickIdResult)
      console.log('form:', form)
      console.log('specsToSave:', specsToSave)
      console.groupEnd()
    }

    let saved = null
    try {
      saved = await createAppliance(supabase, {
        homeId: homeId || null,
        type: eff.type,
        location: eff.location || null,
        nickname: eff.nickname || null,
        make: eff.make,
        model: eff.model || null,
        serial: eff.serial || null,
        manufactured: eff.manufactured || null,
        notes: eff.notes || null,
        status: 'good',
        ...(quickIdResult ? { specifications: specsToSave, spec_source: 'ai_vision' } : {}),
        ...(dataPlateUnavailable ? { data_plate_unavailable: true } : {}),
        createdBy: userId,
      })
    } catch (err) {
      setLoading(false)
      const msg = err?.message || err?.error_description || 'Unknown error'
      console.error('[AddAppliance] save failed:', err)
      setAlert(`Could not save appliance: ${msg}`)
      return
    }
    setLoading(false)
    if (!saved) {
      console.error('[AddAppliance] save returned null (no row)')
      setAlert('Could not save appliance: no row returned from server.')
      return
    }

    // Upload optional photo after we have the appliance ID (placement uuid)
    if (saved?.id && photoFile) {
      try {
        const stripped = await stripExif(photoFile)
        const compressed = await compressImage(stripped, 1600, 0.80)
        const filename = `${userId}-${saved.asset_id}-${Date.now()}.jpg`
        const { error: uploadError } = await supabase.storage
          .from('appliance-photos').upload(filename, compressed, { upsert: true, contentType: 'image/jpeg' })
        if (!uploadError) {
          const { data: urlData } = supabase.storage.from('appliance-photos').getPublicUrl(filename)
          await updateApplianceAsset(supabase, saved.id, {
            photos: [urlData.publicUrl],
            photo_uploaded_at: new Date().toISOString(),
          })
          recordProvenance({
            file: compressed,
            originalFile: photoFile,
            storageBucket: 'appliance-photos',
            storagePath: filename,
            resourceType: 'appliance',
            resourceId: saved.asset_id,
            homeId: homeId || null,
            userId,
          }).catch(() => {})
        }
      } catch { /* photo failure does not block appliance save */ }
    }

    // Fire and forget for the DB write (the EF writes manual_url directly), but
    // surface the outcome to the tour engine and any future listeners via the
    // app event bus — this is the welcome tour's "wow moment". See
    // src/lib/appEvents.js. No await: we don't block the save on the lookup.
    if (saved?.id && eff.make && eff.model) {
      const applianceId = saved.asset_id || saved.id || null
      supabase.functions.invoke('lookup-manual', {
        body: { make: eff.make, model: eff.model, placement_id: saved.id },
      }).then(({ data, error }) => {
        const manualUrl = data?.manual_url || data?.manualUrl || null
        emitTourEvent(TOUR_EVENT_TYPES.MANUAL_LOOKUP_RESOLVED, {
          status: error ? 'error' : (manualUrl ? 'hit' : 'miss'),
          manual_url: manualUrl,
          make: eff.make,
          model: eff.model,
          applianceId,
        })
      }).catch(() => {
        emitTourEvent(TOUR_EVENT_TYPES.MANUAL_LOOKUP_RESOLVED, {
          status: 'error', manual_url: null, make: eff.make, model: eff.model, applianceId,
        })
      })
    }

    // Tour signal: a real appliance was saved — the welcome tour's step 3
    // advances to the wow moment on this. See src/lib/appEvents.js.
    emitTourEvent(TOUR_EVENT_TYPES.APPLIANCE_SAVED, {
      appliance_id: saved.id,
      asset_id: saved.asset_id || null,
    })

    hasSaved.current = true
    onSaved()
  }

  const schedule = maintenanceSchedules[form.type] || maintenanceSchedules.other

  const inp = (field, placeholder, hintable = false) => {
    const suggested = hintable && !!aiSuggested[field]
    return (
      <>
        <input
          style={{
            ...S.input(focus === field),
            ...(suggested ? { borderColor: C.amber, borderWidth: '2px', marginBottom: '4px' } : {}),
          }}
          value={form[field]}
          placeholder={placeholder}
          onChange={set(field)}
          onFocus={() => setFocus(field)}
          onBlur={() => setFocus('')}
        />
        {suggested && (
          <div style={{ fontSize: '11px', color: C.amber, fontFamily: 'Calibri, sans-serif', marginBottom: '10px' }}>
            ✨ AI suggested — please verify
          </div>
        )}
      </>
    )
  }

  return (
    <>
      <style>{`
        @keyframes pulse-amber {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes casavio-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes casavio-instructionFadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes casavio-pillFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes casavio-narrationLineIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes casavio-reviewBtnIn {
          from { opacity: 0; transform: scale(0.88) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes casavio-reviewBtnPulse {
          0%, 100% { transform: scale(1); box-shadow: 0 4px 14px rgba(184,114,31,0.40); }
          50%      { transform: scale(1.035); box-shadow: 0 6px 22px rgba(184,114,31,0.60); }
        }
      `}</style>

      {/* ── PRE-CAMERA LOADING OVERLAY ── covers the chooser so users don't
           see it flash before the camera opens on direct-to-scan entry. */}
      {cameraInit === 'opening' && !showCamera && !cameraPermissionDenied && (
        <div style={{
          position: 'fixed', inset: 0, background: '#000',
          zIndex: 1900, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: '14px',
        }}>
          <div style={S.spinnerBig} />
          <div style={S.openingText}>Opening camera…</div>
        </div>
      )}

      {/* ── CAMERA PERMISSION DENIED MODAL ── */}
      {cameraPermissionDenied && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.7)',
          zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '24px',
        }}>
          <div style={{
            position: 'relative',
            background: C.card, borderRadius: '20px', padding: '32px 28px',
            width: '100%', maxWidth: '360px', textAlign: 'center',
          }}>
            <button
              type="button"
              style={S.modalCloseX}
              onClick={() => { setCameraPermissionDenied(false); onClose() }}
              aria-label="Close and return to dashboard"
            >✕</button>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}><Camera size={48} strokeWidth={1.5} color={C.amber} /></div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: C.brown, fontFamily: f, marginBottom: '10px' }}>
              Camera Access Needed
            </div>
            <div style={{ fontSize: '14px', color: C.muted, fontFamily: 'Calibri, sans-serif', lineHeight: 1.6, marginBottom: '24px' }}>
              Casavio needs camera access to scan appliance data plates. Please allow camera access in your browser settings.
            </div>

            {/* Step by step instructions */}
            <div style={{ textAlign: 'left', background: C.bg, borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
              {[
                { step: '1', text: 'Tap the lock 🔒 or info ⓘ icon in your browser address bar' },
                { step: '2', text: 'Tap Permissions or Site Settings' },
                { step: '3', text: 'Set Camera to Allow' },
                { step: '4', text: 'Refresh the page and try again' },
              ].map(item => (
                <div key={item.step} style={{ display: 'flex', gap: '12px', marginBottom: '10px', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '22px', height: '22px', borderRadius: '50%',
                    background: C.amber, color: '#fff', fontSize: '11px',
                    fontWeight: '700', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', flexShrink: 0, fontFamily: f,
                  }}>{item.step}</div>
                  <div style={{ fontSize: '13px', color: C.brown, fontFamily: 'Calibri, sans-serif', lineHeight: 1.5 }}>
                    {item.text}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                style={{
                  flex: 1, background: 'transparent', border: `1px solid ${C.border}`,
                  borderRadius: '10px', padding: '12px', fontSize: '13px',
                  color: C.muted, cursor: 'pointer', fontFamily: f,
                }}
                onClick={() => { setCameraPermissionDenied(false); handleScanClick() }}
              >
                Use Photo Instead
              </button>
              <button
                style={{
                  flex: 1, background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
                  border: 'none', borderRadius: '10px', padding: '12px',
                  fontSize: '13px', fontWeight: '700', color: C.card,
                  cursor: 'pointer', fontFamily: f,
                }}
                onClick={() => { setCameraPermissionDenied(false); startCamera() }}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── LIVE CAMERA OVERLAY ── tap-to-capture, two-photo flow */}
      {showCamera && (() => {
        const isPhoto1 = scanPhase === 'photo1'
        const isNarrating = scanPhase === 'photo2_narrating'
        const isMatchedBrand = !!(photo2Entry && photo2Entry.brand)
        const instructionText = isPhoto1
          ? 'Take a photo of the front of the appliance'
          : isMatchedBrand
            ? (photo2Entry?.photos?.[1]?.instruction || 'Take a photo of the data plate')
            : 'Take a photo of the data plate'
        return (
        <div style={S.cameraOverlay}>
          <div style={S.cameraHeader}>
            {!isNarrating && (
              <div style={S.stepBadge}>
                {isPhoto1 ? 'PHOTO 1 OF 2' : 'PHOTO 1 ✓ · PHOTO 2 OF 2'}
              </div>
            )}
            {isNarrating && <div />}
            <button
              style={S.cameraClose}
              onClick={() => { stopCamera(); onClose() }}
              aria-label="Cancel scan and close"
            >✕</button>
          </div>

          {!isNarrating && (
            <div style={S.instructionPillWrap}>
              <div
                key={instructionText}
                style={S.instructionPill}
                aria-live="polite"
              >
                {instructionText}
              </div>
            </div>
          )}

          <div style={S.videoContainer}>
            <video
              ref={videoRef}
              style={S.video}
              autoPlay
              playsInline
              muted
            />

            {/* Photo 2 still — covers the live video during narration so the
                user sees what they captured, not the moving camera feed. */}
            {isNarrating && photo2DataUrl && (
              <img src={photo2DataUrl} alt="" style={S.photo2Still} />
            )}

            {torchSupported && !isNarrating && (
              <button style={S.torchBtn(torchOn)} onClick={toggleTorch} aria-label={torchOn ? 'Flashlight on' : 'Flashlight off'}>
                🔦
              </button>
            )}

            {/* Photo 1 thumbnail — visible during photo2 only; hidden during narration. */}
            {photo1ThumbUrl && scanPhase === 'photo2' && (
              <button
                style={S.thumbWrap}
                onClick={() => setRetakeMenuOpen(true)}
                aria-label="Photo 1 — tap to retake"
              >
                <img src={photo1ThumbUrl} alt="" style={S.thumbImg} />
                <span style={S.thumbBadge}>✓ 1</span>
              </button>
            )}

            {/* Subtle background-identifying pill (Photo 1 AI call in-flight) */}
            {backgroundIdentifying && scanPhase === 'photo2' && (
              <div style={S.backgroundIdPill} aria-live="polite">
                <span style={S.miniSpinner} />
                Identifying…
              </div>
            )}

            {/* Narration stack — progressively revealed AI findings over the photo 2 still. */}
            {isNarrating && (
              <div style={S.narrationStack} aria-live="polite">
                {narrationLines.map(line => (
                  <div key={line.id} style={S.narrationLine}>
                    <span style={line.kind === 'found' ? S.narrationIconFound : S.narrationIconMuted} aria-hidden="true">
                      {line.kind === 'found' ? '✓' : '○'}
                    </span>
                    <span style={S.narrationText}>
                      {line.kind === 'found'
                        ? <>Found <strong style={S.narrationField}>{line.label}</strong>: {line.value}</>
                        : line.kind === 'not_visible'
                          ? <><strong style={S.narrationField}>{line.label}</strong>: not visible</>
                          : line.label}
                    </span>
                  </div>
                ))}
                {showReviewBtn && (
                  <>
                    <button style={S.reviewBtn} onClick={handleReviewTap}>
                      {extractionFailed ? 'Continue manually →' : 'Tap to review →'}
                    </button>
                    <button style={S.retakePhoto2Btn} onClick={retakePhoto2}>
                      Retake Photo 2
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Retake confirmation menu */}
            {retakeMenuOpen && (
              <div style={S.retakeOverlay} onClick={() => setRetakeMenuOpen(false)}>
                <div style={S.retakeMenu} onClick={(e) => e.stopPropagation()}>
                  <div style={S.retakeTitle}>Photo 1</div>
                  <img src={photo1ThumbUrl} alt="" style={S.retakePreview} />
                  <button style={S.retakePrimary} onClick={retakePhoto1}>Retake Photo 1</button>
                  <button style={S.retakeSecondary} onClick={() => setRetakeMenuOpen(false)}>Looks good</button>
                </div>
              </div>
            )}
          </div>

          {/* Bottom control bar: hidden during narration (the user's next action lives in the narration overlay). */}
          {!isNarrating && (
            <div style={S.cameraBottom}>
              {!torchSupported && isMobile && (
                <div style={S.lightingHint}>
                  {isIOS
                    ? '📱 Dim lighting? Open Control Center, tap the flashlight icon, then return to Casavio.'
                    : '📱 Dim lighting? Use your phone\'s flashlight from your home screen, then return to Casavio.'}
                </div>
              )}

              <button
                style={S.shutter(scanPhase === 'photo1' || scanPhase === 'photo2')}
                onClick={handleShutterTap}
                aria-label={scanPhase === 'photo2' ? 'Take Photo 2' : 'Take Photo 1'}
              >
                <span style={S.shutterInner} />
              </button>

              {/* Equal-weight escape hatches: camera-roll + manual entry.
                  Casavio principle from yesterday's session — wherever
                  camera-roll is offered, manual-entry is offered at equal
                  visual weight. */}
              <div style={S.cameraEscapeRow}>
                <button
                  style={S.uploadFallback}
                  onClick={() => { stopCamera(); handleScanClick() }}
                >
                  Choose from camera roll
                </button>
                <span style={S.cameraEscapeSep} aria-hidden="true">·</span>
                <button
                  style={S.uploadFallback}
                  onClick={() => { stopCamera(); setStep(2) }}
                >
                  Type manually
                </button>
              </div>

              {/* "Can't find the data plate" — only visible during Photo 2,
                  since Photo 1 is the front-of-appliance shot which is always
                  available. */}
              {scanPhase === 'photo2' && (
                <button
                  type="button"
                  style={S.skipPlateLink}
                  onClick={() => setSkipConfirmOpen(true)}
                >
                  Can't find the data plate
                </button>
              )}
            </div>
          )}

          {/* Skip-data-plate inline confirmation overlay */}
          {skipConfirmOpen && (
            <div style={S.skipConfirmOverlay} onClick={() => setSkipConfirmOpen(false)}>
              <div style={S.skipConfirmCard} onClick={(e) => e.stopPropagation()}>
                <div style={S.skipConfirmTitle}>Skip data plate?</div>
                <div style={S.skipConfirmBody}>
                  You can add details manually on the next screen. Photo 1 will still be saved.
                </div>
                <div style={S.skipConfirmRow}>
                  <button style={S.skipConfirmCancel} onClick={() => setSkipConfirmOpen(false)}>
                    Cancel
                  </button>
                  <button style={S.skipConfirmPrimary} onClick={confirmSkipDataPlate}>
                    Skip
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        )
      })()}

      <div style={S.overlay} onClick={onClose}>
        <div style={S.modal} onClick={e => e.stopPropagation()}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onClick={e => e.stopPropagation()}
            onChange={handleImageCapture}
          />
          <input
            ref={quickIdCameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onClick={e => e.stopPropagation()}
            onChange={handleQuickIdentifyCapture}
          />
          <input
            ref={quickIdGalleryInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onClick={e => e.stopPropagation()}
            onChange={handleQuickIdentifyCapture}
          />

          <div style={S.handle} />
          <div style={S.stepDots}>
            {[1, 2, 3].map(n => (
              <div key={n} style={S.dot(step === n, step > n)} />
            ))}
          </div>

          {/* ── STEP 1 ── */}
          {step === 1 && (
            <>
              <div style={S.title}>Add an Appliance</div>
              <div style={S.subtitle}>
                {typeLocked
                  ? 'Snap a photo or scan the data plate. Or type details manually.'
                  : 'Snap a photo, scan the data plate, or pick a type manually'}
              </div>

              {/* Quick Identify in progress */}
              {quickIdentifying && (
                <div style={{ textAlign: 'center', padding: '20px', color: C.amber, fontFamily: 'Calibri, sans-serif' }}>
                  ⟳ Identifying appliance...
                </div>
              )}

              {/* Quick Identify — medium confidence Yes/No prompt */}
              {quickIdConfirmNeeded && quickIdResult && !quickIdentifying && (
                <div style={S.recoveryPanel}>
                  <div style={S.recoveryTitle}>
                    Looks like a {quickIdResult.make || 'unknown brand'} {quickIdResult.type || 'appliance'}. Is that right?
                  </div>
                  {quickIdPhotoPreview && (
                    <img src={quickIdPhotoPreview} alt="Your photo" style={S.recoveryPhoto} />
                  )}
                  {quickIdResult.visual_notes && (
                    <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '12px', fontStyle: 'italic' }}>
                      {quickIdResult.visual_notes}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      style={{ flex: 1, background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 18px', fontSize: '13px', color: C.muted, cursor: 'pointer', fontFamily: f }}
                      onClick={confirmQuickIdNo}
                    >
                      No, I'll pick
                    </button>
                    <button
                      style={{ flex: 1, background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`, border: 'none', borderRadius: '8px', padding: '10px 18px', fontSize: '13px', fontWeight: '700', color: C.card, cursor: 'pointer', fontFamily: f }}
                      onClick={confirmQuickIdYes}
                    >
                      Yes, that's it
                    </button>
                  </div>
                </div>
              )}

              {/* Quick Identify — low confidence / unidentifiable */}
              {quickIdLowConf && !quickIdentifying && !quickIdConfirmNeeded && (
                <div style={S.recoveryPanel}>
                  <div style={S.recoveryTitle}>We couldn't tell from this photo</div>
                  {quickIdPhotoPreview && (
                    <img src={quickIdPhotoPreview} alt="Your photo" style={S.recoveryPhoto} />
                  )}
                  <div style={{ fontSize: '13px', color: C.brown, fontFamily: 'Calibri, sans-serif', marginBottom: '12px', lineHeight: 1.5 }}>
                    Try a different angle from a few feet back, or fill in the details manually.
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      style={{ flex: 1, background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 18px', fontSize: '13px', color: C.muted, cursor: 'pointer', fontFamily: f }}
                      onClick={() => { resetQuickIdState(); setStep(2) }}
                    >
                      Fill in manually
                    </button>
                    <button
                      style={{ flex: 1, background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`, border: 'none', borderRadius: '8px', padding: '10px 18px', fontSize: '13px', fontWeight: '700', color: C.card, cursor: 'pointer', fontFamily: f }}
                      onClick={() => { resetQuickIdState(); openQuickIdCameraPicker() }}
                    >
                      Try another photo
                    </button>
                  </div>
                </div>
              )}

              {/* Scanning in progress (data-plate upload) */}
              {scanning && (
                <div style={{ textAlign: 'center', padding: '20px', color: C.amber, fontFamily: 'Calibri, sans-serif' }}>
                  ⟳ Reading data plate...
                </div>
              )}

              {/* Scan result */}
              {scanResult && !scanning && (
                <div style={S.scanResult}>
                  <div style={S.scanResultTitle}>✓ Plate Scanned Successfully</div>
                  {scanResult.make && <div style={S.scanResultField}><span style={S.scanResultLabel}>Brand</span><span>{scanResult.make}</span></div>}
                  {scanResult.model && <div style={S.scanResultField}><span style={S.scanResultLabel}>Model</span><span>{scanResult.model}</span></div>}
                  {scanResult.serial && <div style={S.scanResultField}><span style={S.scanResultLabel}>Serial</span><span>{scanResult.serial}</span></div>}
                  {scanResult.manufactured && <div style={S.scanResultField}><span style={S.scanResultLabel}>Year</span><span>{scanResult.manufactured}</span></div>}
                  <div
                    style={{ fontSize: '12px', color: C.amber, fontFamily: 'Calibri, sans-serif', marginTop: '8px', cursor: 'pointer' }}
                    onClick={startCamera}
                  >
                    Rescan ↺
                  </div>
                </div>
              )}

              {/* Scan-fail recovery panel */}
              {scanFailed && !scanning && !scanResult && (
                <div style={S.recoveryPanel}>
                  <div style={S.recoveryTitle}>Couldn't read the data plate</div>
                  {photoPreview && (
                    <img src={photoPreview} alt="Last captured frame" style={S.recoveryPhoto} />
                  )}
                  <div style={S.recoveryOptions}>
                    {[
                      'Retake the photo with better lighting',
                      'Type the brand name below — we\'ll look again with your hint',
                      'Skip this and just save the photo for now',
                    ].map((text, i) => (
                      <div key={i} style={S.recoveryOption}>
                        <div style={S.recoveryNum}>{i + 1}</div>
                        <span>{text}</span>
                      </div>
                    ))}
                  </div>
                  <button style={S.retakeBtn} onClick={() => { setScanFailed(false); startCamera() }}>
                    📷 Retake with better lighting
                  </button>
                  {photoPreview && (
                    <div style={S.photoPreserved}>
                      ✓ Photo saved — no need to retake unless lighting was the problem
                    </div>
                  )}
                </div>
              )}

              {/* Entry options — hidden during any scan/identify flow */}
              {!scanning && !scanResult && !scanFailed && !quickIdentifying && !quickIdConfirmNeeded && !quickIdLowConf && (
                <>
                  {/* Primary: Live camera scan — calls identify-appliance with full extraction + specs */}
                  <div style={S.scanBanner} onClick={startCamera}>
                    <div style={S.scanIcon}><Camera size={32} strokeWidth={1.5} color={C.amber} /></div>
                    <div style={S.scanText}>
                      <div style={S.scanTitle}>🔍 Scan the appliance (recommended)</div>
                      <div style={S.scanSub}>Auto-identifies make, model, and specs. No tapping.</div>
                    </div>
                    <div style={S.scanArrow}>→</div>
                  </div>

                  <div style={S.entryLinks}>
                    <button type="button" style={S.entryLink} onClick={openQuickIdGalleryPicker}>
                      Choose from camera roll
                    </button>
                    <button type="button" style={S.entryLink} onClick={() => setStep(2)}>
                      Type manually
                    </button>
                  </div>
                </>
              )}

              {alert && <div style={S.alert}>{alert}</div>}

              {!typeLocked && (
                <>
                  <div style={S.divider}>
                    <div style={S.dividerLine} />
                    <div style={S.dividerText}>SELECT TYPE</div>
                    <div style={S.dividerLine} />
                  </div>

                  <div style={S.typeGrid}>
                    {TYPES.map(t => {
                      const TIcon = t.Icon
                      return (
                        <div key={t.id} style={S.typeCard(form.type === t.id)} onClick={() => selectType(t.id)}>
                          <div style={S.typeIcon}><TIcon size={24} strokeWidth={1.5} color={form.type === t.id ? C.amber : C.muted} /></div>
                          <div style={S.typeLabel(form.type === t.id)}>{t.label}</div>
                        </div>
                      )
                    })}
                  </div>
                </>
              )}

              <div style={S.btnRow}>
                <button style={S.btnBack} onClick={onClose}>Cancel</button>
                {!typeLocked && (
                  <button style={S.btnNext(!canProceed1)} onClick={() => canProceed1 && setStep(2)}>
                    Next →
                  </button>
                )}
              </div>
            </>
          )}

          {/* ── STEP 2 ── */}
          {step === 2 && (() => {
            const initialTypeLabel = TYPES.find(t => t.id === initialType)?.label
            const currentTypeLabel = TYPES.find(t => t.id === form.type)?.label
            const typeMismatch = !!(initialType && form.type && initialType !== form.type && initialTypeLabel && currentTypeLabel)
            return (
            <>
              <div style={S.step2Header}>
                <div style={S.title}>Appliance Details</div>
                <button
                  type="button"
                  style={S.step2CloseX}
                  onClick={silentSaveAndClose}
                  aria-label="Save and close"
                >✕</button>
              </div>
              <div style={S.subtitle}>
                {scanResult ? 'We pre-filled what we found — review and add anything missing' : 'Enter the details from the label or manual'}
              </div>

              {typeMismatch && (
                <div style={{ background: '#FFF8E7', border: `1px solid ${C.amber}44`, borderRadius: '10px', padding: '10px 14px', fontSize: '12px', color: C.amber, fontFamily: 'Calibri, sans-serif', marginBottom: '16px' }}>
                  We detected this is a <strong>{currentTypeLabel}</strong> instead of a <strong>{initialTypeLabel}</strong> — update if needed.
                </div>
              )}

              {dataPlateUnavailable && (
                <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '10px 14px', fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '16px' }}>
                  No data plate captured — fill in details manually.
                </div>
              )}

              {scanResult && (
                <div style={{ background: '#EBF5EF', border: `1px solid ${C.success}33`, borderRadius: '10px', padding: '10px 14px', fontSize: '12px', color: C.success, fontFamily: 'Calibri, sans-serif', marginBottom: '16px' }}>
                  ✓ Fields pre-filled from scan — verify and add any missing info
                </div>
              )}
              {scanFailed && !scanResult && (
                <div style={{ background: '#FFF8E7', border: `1px solid ${C.amber}44`, borderRadius: '10px', padding: '10px 14px', fontSize: '12px', color: C.amber, fontFamily: 'Calibri, sans-serif', marginBottom: '16px' }}>
                  Scan couldn't read all details — type the brand name below and we'll look again. Make is optional if you want to save with just the photo for now.
                </div>
              )}

              {hintScanning && (
                <div style={{ background: '#FFF8E7', border: `1px solid ${C.amber}44`, borderRadius: '10px', padding: '10px 14px', fontSize: '12px', color: C.amber, fontFamily: 'Calibri, sans-serif', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ animation: 'pulse-amber 1s infinite' }}>⟳</span> Looking again with your hint...
                </div>
              )}

              {hintSuccess && !hintScanning && (
                <div style={{ background: '#EBF5EF', border: `1px solid ${C.success}33`, borderRadius: '10px', padding: '10px 14px', fontSize: '12px', color: C.success, fontFamily: 'Calibri, sans-serif', marginBottom: '16px' }}>
                  ✓ Found more details with your hint — fields marked ✨ were AI-suggested, please verify.
                </div>
              )}

              {makeMismatch && !hintScanning && (
                <div style={{ background: '#FFF8E7', border: `1px solid ${C.amber}44`, borderRadius: '10px', padding: '10px 14px', fontSize: '12px', color: C.amber, fontFamily: 'Calibri, sans-serif', marginBottom: '16px' }}>
                  We detected this might be a {makeMismatch} — please double-check the brand.
                </div>
              )}

              {/* Quick Identify result — photo + visual_notes for user to verify */}
              {quickIdResult && quickIdPhotoPreview && (
                <div style={{ background: '#FBF3E8', border: `1px solid ${C.amber}33`, borderRadius: '12px', padding: '12px', marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <img src={quickIdPhotoPreview} alt="Your photo" style={{ width: '72px', height: '72px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0 }} />
                  <div style={{ flex: 1, fontSize: '12px', color: C.brown, fontFamily: 'Calibri, sans-serif', lineHeight: 1.5 }}>
                    <div style={{ fontWeight: '700', marginBottom: '4px', color: C.amber }}>✨ AI identified from photo</div>
                    {quickIdResult.visual_notes && (
                      <div style={{ marginBottom: '4px' }}>{quickIdResult.visual_notes}</div>
                    )}
                    {summarizeSpecs(quickIdResult.specifications) && (
                      <div style={{ marginTop: '4px', fontSize: '12px', color: C.brown }}>
                        {summarizeSpecs(quickIdResult.specifications)}
                      </div>
                    )}
                    {quickIdResult.approximate_age && (
                      <div style={{ color: C.muted }}>Approx. age: {quickIdResult.approximate_age}</div>
                    )}
                  </div>
                </div>
              )}

              {alert && <div style={S.alert}>{alert}</div>}

              <label style={S.label}>Appliance Name *</label>
              {inp('name', 'e.g. Refrigerator, Main HVAC')}
              <label style={S.label}>Nickname (optional)</label>
              {inp('nickname', 'e.g. Kitchen Fridge, Upstairs AC')}
              <label style={S.label}>Location</label>
              {inp('location', 'e.g. Kitchen, Garage, Basement')}

              <div style={S.row}>
                <div>
                  <label style={S.label}>Make / Brand *</label>
                  {inp('make', 'e.g. LG, Whirlpool')}
                </div>
                <div>
                  <label style={S.label}>Model</label>
                  {inp('model', 'Model number', true)}
                </div>
              </div>
              <div style={S.row}>
                <div>
                  <label style={S.label}>Serial Number</label>
                  {inp('serial', 'Serial #', true)}
                </div>
                <div>
                  <label style={S.label}>Year Manufactured</label>
                  <input
                    style={{
                      ...S.input(focus === 'manufactured'),
                      ...(aiSuggested.manufactured ? { borderColor: C.amber, borderWidth: '2px', marginBottom: '4px' } : {}),
                    }}
                    value={form.manufactured}
                    placeholder="e.g. 2018"
                    inputMode="numeric"
                    onChange={set('manufactured')}
                    onFocus={() => setFocus('manufactured')}
                    onBlur={() => setFocus('')}
                  />
                  {aiSuggested.manufactured && (
                    <div style={{ fontSize: '11px', color: C.amber, fontFamily: 'Calibri, sans-serif', marginBottom: '10px' }}>
                      ✨ AI suggested — please verify
                    </div>
                  )}
                </div>
              </div>

              {/* Subtle data-plate link — only when scan hasn't already run */}
              {!scanResult && !scanFailed && (
                <div
                  onClick={openDataPlateScanFromStep2}
                  style={{
                    fontSize: '12px', color: C.amber, fontFamily: 'Calibri, sans-serif',
                    cursor: 'pointer', marginBottom: '14px', marginTop: '-4px',
                    display: 'inline-block', borderBottom: `1px dashed ${C.amber}55`,
                  }}
                >
                  Want exact model &amp; serial? Read the data plate →
                </div>
              )}

              <label style={S.label}>Photo (optional)</label>
              <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoSelect} />
              {photoPreview ? (
                <div style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', height: '120px', background: C.bg, marginBottom: '14px' }}>
                  <img src={photoPreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  <button
                    onClick={clearPhoto}
                    style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '50%', width: 24, height: 24, color: '#fff', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
                  >×</button>
                </div>
              ) : (
                <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, border: `2px dashed ${C.border}`, borderRadius: '10px', padding: '16px', cursor: 'pointer', color: C.muted, fontSize: '13px', fontFamily: 'Calibri, sans-serif', marginBottom: '14px' }}>
                  <Camera size={16} strokeWidth={1.5} color={C.muted} />
                  Add a photo
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoSelect} />
                </label>
              )}

              <label style={S.label}>Notes</label>
              <textarea
                style={{ ...S.input(focus === 'notes'), resize: 'vertical', minHeight: '80px' }}
                value={form.notes}
                placeholder="Warranty info, service history, anything useful..."
                onChange={set('notes')}
                onFocus={() => setFocus('notes')}
                onBlur={() => setFocus('')}
              />

              <div style={S.btnRow}>
                <button style={S.btnBack} onClick={() => setStep(1)}>← Back</button>
                <button style={S.btnNext(!canProceed2)} onClick={() => canProceed2 && setStep(3)}>
                  Review →
                </button>
              </div>
            </>
            )
          })()}

          {/* ── STEP 3 ── */}
          {step === 3 && (
            <>
              <div style={S.title}>Maintenance Schedule</div>
              <div style={S.subtitle}>Here's what we'll track for your {form.name}</div>

              {schedule.map((item, i) => (
                <div key={i} style={S.scheduleCard}>
                  <div style={S.scheduleTask}>{item.task}</div>
                  <div style={S.scheduleMeta}>{item.interval}</div>
                  <div style={S.tag(item.type)}>
                    {item.type === 'auto_order' ? 'Order Parts' : item.type === 'contractor' ? 'Contractor Bid' : 'DIY'}
                  </div>
                </div>
              ))}

              {alert && <div style={{ ...S.alert, marginTop: '14px' }}>{alert}</div>}

              <div style={S.btnRow}>
                <button style={S.btnBack} onClick={() => setStep(2)}>← Back</button>
                <button style={S.btnNext(loading)} onClick={handleSave}>
                  {loading ? 'Saving...' : 'Save Appliance'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}